import { useEffect } from 'react'

import { useAtomValue, useSetAtom } from 'jotai'

import { clockAtom } from '@/shared/store/clock.store'
import { tickControlAtom } from '@/shared/store/tick.store'

import { rundownAtom } from '@/store/rundown.store'
import { isRunningAtom, runnerAtom } from '@/store/runner.store'
import { timestampsAtom } from '@/store/timestamps.store'

import { cueTimingActions } from '@/features/rundown/cue/cue-timing.store'
import { useCuesDerived } from '@/features/rundown/cue/cue.derived'
import { findNextHardStartCueId } from '@/features/rundown/cue/cue.helpers'
import { getCue } from '@/features/rundown/data/cues.store'

/**
 * Drives rundown clock and timestamp computation while the rundown is mounted.
 *
 * - The wall clock ticks every 500ms (`clockAtom`).
 * - While the runner is running, a 300ms tick (`tickControlAtom`) recomputes
 *   timestamps continuously.
 * - Timestamps are also recomputed whenever the rundown, runner or cues change.
 * - The rundown-wide `nextHardStartCueId` is recomputed whenever the runner or
 *   cues change.
 *
 * Mount only once the session is ready so the first computation has data.
 */
export function useRundownTiming() {
  const startClock = useSetAtom(clockAtom)
  const setTickControl = useSetAtom(tickControlAtom)
  const createTimestamps = useSetAtom(timestampsAtom)

  const isRunning = useAtomValue(isRunningAtom)
  const rundown = useAtomValue(rundownAtom)
  const runner = useAtomValue(runnerAtom)
  const { flatCueIds, sortedCues } = useCuesDerived()

  // useEffect: owns a setInterval for the lifetime of the page.
  useEffect(() => {
    const cleanup = startClock()
    return () => cleanup()
  }, [startClock])

  // useEffect: starts/stops the 300ms tick interval in the tick store based on
  // running state, and must stop it on unmount.
  useEffect(() => {
    setTickControl(isRunning)
    return () => setTickControl(false)
  }, [isRunning, setTickControl])

  // useEffect: `timestampsAtom` is a write-triggered cache shared with the tick
  // interval, not a derived atom, so it must be explicitly recomputed when its
  // inputs change.
  useEffect(() => {
    createTimestamps()
  }, [createTimestamps, rundown, runner, sortedCues])

  // useEffect: `nextHardStartCueId` searches forward from the running cue, so
  // it joins cue data with the runner and cannot be derived alongside the cue
  // lists. Computed here once so rows only read the result. `sortedCues` is the
  // trigger for a cue document edit; a startMode change does not move the order.
  useEffect(() => {
    cueTimingActions.setNextHardStartCueId(
      runner
        ? findNextHardStartCueId(
            flatCueIds,
            getCue,
            runner.timesnap.cueId ?? null,
          )
        : null,
    )
  }, [flatCueIds, sortedCues, runner])
}
